import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Settings, Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
const ROLES = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'sales', label: 'Sales' },
    { value: 'inventory_manager', label: 'Inventory Manager' },
];

export default function AdminUsers() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ email: '', password: '', name: '', role: 'sales', active: true });

    const load = async () => {
        setLoading(true);
        try { const r = await api.get('/admin/users'); setItems(r.data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm({ email: '', password: '', name: '', role: 'sales', active: true }); setDialog(true); };
    const openEdit = (u) => { setEditing(u); setForm({ email: u.email, password: '', name: u.name, role: u.role, active: u.active }); setDialog(true); };

    const save = async () => {
        try {
            if (editing) {
                const body = { name: form.name, role: form.role, active: form.active };
                if (form.password) body.password = form.password;
                await api.patch(`/admin/users/${editing.id}`, body);
            } else {
                await api.post('/admin/users', form);
            }
            toast.success('Saved'); setDialog(false); load();
        } catch (e) { toast.error(errMsg(e, 'Failed')); }
    };

    const doDelete = async (u) => {
        if (!window.confirm(`Delete user ${u.email}?`)) return;
        try { await api.delete(`/admin/users/${u.id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(errMsg(e, 'Failed')); }
    };

    return (
        <AppShell pageTitle="Admin Users" pageActions={
            <Button onClick={openNew} data-testid="add-user-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}><Plus className="h-4 w-4 mr-2" />Add User</Button>
        }>
            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={Settings} title="No users" description="Add your first user." />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="users-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">Name</th>
                                <th className="text-left font-medium">Email</th>
                                <th className="text-left font-medium">Role</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-right p-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(u => (
                                <tr key={u.id} className="border-b hover:bg-neutral-50" data-testid={`user-row-${u.email}`}>
                                    <td className="p-3 font-medium">{u.name}</td>
                                    <td>{u.email}</td>
                                    <td className="capitalize">{u.role.replace('_', ' ')}</td>
                                    <td>{u.active ? <span className="text-green-700 text-xs">Active</span> : <span className="text-neutral-500 text-xs">Inactive</span>}</td>
                                    <td className="p-3 text-right">
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => doDelete(u)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? 'Edit User' : 'New Admin User'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
                        <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="user-email-input" /></div>
                        <div><Label>{editing ? 'New Password (leave blank to keep)' : 'Password'}</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
                        <div>
                            <Label>Role</Label>
                            <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        {editing && <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} /><Label>Active</Label></div>}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="save-user-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
